// src/sim/ai.ts -- pure ballistic AI with skill levels
// Computes (angle, power, weaponId) for an AI tank's turn.

import type { GameState, Tank } from './state';
import { WEAPONS_BY_ID } from './weapons';

export type AILevel = 'human' | 'easy' | 'medium' | 'hard' | 'expert';

export const AI_LEVELS: readonly AILevel[] = ['human', 'easy', 'medium', 'hard', 'expert'] as const;

export function aiLabel(level: AILevel): string {
  switch (level) {
    case 'human': return 'HUMAN';
    case 'easy': return 'AI EASY';
    case 'medium': return 'AI MED';
    case 'hard': return 'AI HARD';
    case 'expert': return 'AI EXPERT';
  }
}

export function nextAILevel(level: AILevel): AILevel {
  const i = AI_LEVELS.indexOf(level);
  return AI_LEVELS[(i + 1) % AI_LEVELS.length];
}

interface Plan {
  angleDeg: number;   // 0..180 (UI convention)
  power: number;      // 0..1000
  weaponId: string;
}

/** Choose an AI move for the given tank. */
export function planAIMove(state: GameState, shooter: Tank, level: AILevel): Plan {
  // 1. Pick weapon based on skill
  const weaponId = pickWeapon(shooter, level);

  // 2. Pick target — alive enemy with lowest HP (smarter levels prefer kill shot)
  const enemies = state.tanks.filter(t => t.alive && t.id !== shooter.id);
  if (enemies.length === 0) {
    return { angleDeg: 90, power: 500, weaponId };
  }
  let target: Tank;
  if (level === 'easy') {
    // random target
    target = enemies[Math.floor(Math.random() * enemies.length)];
  } else {
    // lowest HP, ties broken by closest
    enemies.sort((a, b) => a.hp - b.hp || Math.abs(a.x - shooter.x) - Math.abs(b.x - shooter.x));
    target = enemies[0];
  }

  // 3. Brute-force search for best (angle, power) that lands closest to target
  const g = state.world.gravity;
  const wind = state.world.wind;
  const sx = shooter.x, sy = shooter.y - 4;
  const tx = target.x, ty = target.y;

  let best = { angle: 90, power: 500, dist: Infinity };

  // Angle range biased toward target side
  const angleMin = 20, angleMax = 160, angleStep = 4;
  const powerMin = 200, powerMax = 1000, powerStep = 25;

  for (let ang = angleMin; ang <= angleMax; ang += angleStep) {
    const rad = -ang * Math.PI / 180;
    const cosA = Math.cos(rad), sinA = Math.sin(rad);
    for (let pw = powerMin; pw <= powerMax; pw += powerStep) {
      const v0 = 75 + (pw / 1000) * 525;
      const vx0 = cosA * v0;
      const vy0 = sinA * v0;
      // Solve for time when y(t) = ty: 0.5*g*t² + vy0*t + (sy - ty) = 0
      const a = 0.5 * g;
      const b = vy0;
      const c = sy - ty;
      const disc = b * b - 4 * a * c;
      if (disc < 0) continue;
      const t = (-b + Math.sqrt(disc)) / (2 * a);
      if (t <= 0) continue;
      const xLand = sx + vx0 * t + 0.5 * wind * t * t;
      const dist = Math.abs(xLand - tx);
      if (dist < best.dist) {
        best = { angle: ang, power: pw, dist };
      }
    }
  }

  // 4. Add skill-based error
  const noise = noiseForLevel(level);
  let angleDeg = best.angle + (Math.random() - 0.5) * 2 * noise.angle;
  let power = best.power + (Math.random() - 0.5) * 2 * noise.power;
  angleDeg = Math.max(0, Math.min(180, Math.round(angleDeg)));
  power = Math.max(0, Math.min(1000, Math.round(power)));

  return { angleDeg, power, weaponId };
}

function noiseForLevel(level: AILevel): { angle: number; power: number } {
  switch (level) {
    case 'expert': return { angle: 0.5, power: 5 };
    case 'hard': return { angle: 2, power: 20 };
    case 'medium': return { angle: 6, power: 60 };
    case 'easy': return { angle: 15, power: 150 };
    default: return { angle: 0, power: 0 };
  }
}

function pickWeapon(tank: Tank, level: AILevel): string {
  // Prefer big guns at higher skill; never expend the only nuke at easy
  const have = (id: string) => (tank.inventory[id] ?? 0) > 0;
  const candidates: string[] = [];
  if (level === 'expert' || level === 'hard') {
    candidates.push('deaths-head', 'nuke', 'baby-nuke', 'mirv', 'heavy-roller', 'missile', 'baby-missile');
  } else if (level === 'medium') {
    candidates.push('baby-nuke', 'mirv', 'roller', 'missile', 'baby-missile');
  } else {
    candidates.push('missile', 'baby-missile');
  }
  for (const id of candidates) {
    if (have(id) && WEAPONS_BY_ID[id]) return id;
  }
  return 'baby-missile';
}
